import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export const Modal = ({ props }) => {
  const { showModal, setShowModal, name, delete: onDelete } = props || {};
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => e.key === "Escape" && setShowModal?.();
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    const t = requestAnimationFrame(() => cancelRef.current?.focus?.());
    return () => {
      window.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = prevOverflow;
      cancelAnimationFrame(t);
    };
  }, [showModal, setShowModal]);

  if (!showModal) return null;

  return createPortal(
    <div className="fixed inset-0 z-[2147483647]">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={() => setShowModal?.()}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        className="absolute inset-0 flex min-h-screen items-center justify-center p-4"
      >
        <div
          className="relative w-full max-w-md origin-center rounded-2xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5 transition-transform duration-200 ease-out data-[enter=true]:scale-100 data-[enter=false]:scale-95"
          data-enter={true}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -inset-1 rounded-3xl bg-gradient-to-tr from-emerald-200/40 via-amber-200/40 to-purple-200/40 blur-xl"
          />
          <div className="relative flex items-start justify-end px-4 pt-4">
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowModal?.()}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          <div className="relative px-6 pb-6 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600 ring-1 ring-red-100">
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h3 id="modal-title" className="text-lg font-semibold tracking-tight text-gray-900">
              Delete this item?
            </h3>
            <p id="modal-desc" className="mt-2 text-sm text-gray-700">
              You’re about to delete{" "}
              <span className="font-medium text-emerald-700">{name}</span>. This action can’t be undone.
            </p>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                ref={cancelRef}
                onClick={() => setShowModal?.()}
                className="inline-flex items-center justify-center rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                No, cancel
              </button>
              <button
                type="button"
                onClick={() => onDelete?.()}
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
              >
                <span className="absolute inset-0 -z-10 bg-gradient-to-r from-red-500 via-amber-500 to-purple-500 opacity-0 transition group-hover:opacity-100" />
                Yes, I’m sure
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
